import { Box, Chip, Stack, Tooltip } from "@mui/material";
import {
  FeatureFlagKey,
  FeatureFlagType,
  getFeatureFlagMeta,
  KNOWN_FEATURE_FLAG_KEYS,
} from "encamp-shared/src/featureFlags";

import { EnabledFlagsTooltipBody } from "./FeatureFlagTogglePanel";

/** Minimal shape of a tenant config row needed to read flag values. */
export type TenantConfigForFlags = {
  key: string;
  value: boolean;
};

/**
 * Strip a trailing " module" token so compact contexts (grid chips,
 * tooltips) don't double up with the section header. Internal labels
 * elsewhere (e.g. the toggle panel) keep the full "… module" form.
 */
const stripModuleSuffix = (label: string): string => label.replace(/\s+module$/i, "");

/**
 * Compute the list of module labels enabled for a tenant, with the
 * trailing " module" token stripped. Used as the `valueGetter` source
 * for the "Feature Flags" column so sort/CSV-export show "Tasks, Tier
 * II, …" rather than `[object Object]`.
 *
 * Tenants missing a TenantConfig row fall back to the flag's
 * `defaultValue` from `getFeatureFlagMeta`, mirroring how the toggle
 * panel computes effective state.
 */
export function modulesEnabledLabels(tenantConfigs: readonly TenantConfigForFlags[]): string[] {
  const explicitByKey = new Map(tenantConfigs.map((c) => [c.key, c.value]));
  return KNOWN_FEATURE_FLAG_KEYS.filter((k) => {
    if (getFeatureFlagMeta(k).type !== FeatureFlagType.Module) return false;
    return explicitByKey.get(k) ?? getFeatureFlagMeta(k).defaultValue;
  }).map((k) => stripModuleSuffix(getFeatureFlagMeta(k).label));
}

/**
 * Render the per-tenant feature-flag chip strip used on the Staff
 * Feature Flags page and the Staff Organizations page.
 *
 *   - One outlined chip per enabled top-level Module flag.
 *     Modules with no enabled add-ons render the bare module name.
 *     Modules with N enabled add-ons render the module name plus a
 *     small avatar circle showing the count.
 *   - A trailing "Other" chip aggregating all enabled `FeatureFlagType.Other`
 *     flags. Only rendered when at least one Other flag is on.
 *
 * Hovering any chip shows the list of enabled flags (or add-ons) that
 * the count represents, via {@link EnabledFlagsTooltipBody}.
 */
export function TenantFeatureFlagChips({
  tenantConfigs,
}: {
  tenantConfigs: readonly TenantConfigForFlags[];
}) {
  const explicitByKey = new Map(tenantConfigs.map((c) => [c.key, c.value]));

  const isOn = (key: FeatureFlagKey): boolean =>
    explicitByKey.get(key) ?? getFeatureFlagMeta(key).defaultValue;

  const modules = KNOWN_FEATURE_FLAG_KEYS.filter(
    (k) => getFeatureFlagMeta(k).type === FeatureFlagType.Module
  );
  const enabledOtherFlags = KNOWN_FEATURE_FLAG_KEYS.filter(
    (k) => getFeatureFlagMeta(k).type === FeatureFlagType.Other && isOn(k)
  );

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {modules.map((moduleKey) => {
        if (!isOn(moduleKey)) return null;
        const shortLabel = stripModuleSuffix(getFeatureFlagMeta(moduleKey).label);
        const enabledChildren = KNOWN_FEATURE_FLAG_KEYS.filter(
          (k) => getFeatureFlagMeta(k).parentKey === moduleKey && isOn(k)
        );
        return (
          <CategoryChip
            key={moduleKey}
            label={shortLabel}
            enabledKeys={enabledChildren}
            // Module chips lead with "<module> enabled" so a chip on
            // its own (no add-ons, just the module turned on) reads
            // unambiguously, then list any enabled add-ons under a
            // <module>-prefixed header.
            tooltipLead={`${shortLabel} enabled`}
            tooltipHeader={`${shortLabel} add-ons enabled:`}
            emptyTooltip={`${shortLabel} enabled`}
          />
        );
      })}
      {enabledOtherFlags.length > 0 && (
        <CategoryChip
          label="Other"
          enabledKeys={enabledOtherFlags}
          // No `tooltipLead` here — the "Other" chip aggregates flags
          // that aren't part of any module, so there's no parent to
          // call out separately.
          tooltipHeader="Flags enabled:"
          emptyTooltip="No flags enabled"
          // Caller-only: the "Other" chip has no standalone "Other"
          // flag, so the count IS the chip's purpose. Force the
          // count avatar even when the same logic would suppress it
          // for module chips.
          alwaysShowCount
        />
      )}
    </Stack>
  );
}

/**
 * Renders one category chip (module or "Other") with the count of
 * enabled flags in that category as a small "+N" pill positioned at
 * the right edge of the chip (module name first, count after — reads
 * as "Tasks +2" rather than "+2 Tasks").
 *
 * Implementation note: MUI's `<Chip avatar>` slot only renders on the
 * left, so we compose the count into the `label` slot via a Stack to
 * put it on the right. We also override the chip's right padding to
 * a tighter value so the count pill nestles flush with the chip edge.
 *
 * Counting convention:
 *   - Module chips: the pill shows the *number of enabled add-ons* as
 *     "+N". The chip's presence already communicates "module on", so
 *     a module with no add-ons enabled renders the bare module name
 *     with no pill.
 *   - The "Other" chip: the pill shows the *total enabled Other
 *     flags* as "+N". There's no standalone "Other" flag for the chip
 *     to represent, so we always show the count via `alwaysShowCount`.
 */
function CategoryChip({
  label,
  enabledKeys,
  tooltipHeader,
  tooltipLead,
  emptyTooltip,
  alwaysShowCount = false,
}: {
  label: string;
  enabledKeys: FeatureFlagKey[];
  tooltipHeader: string;
  /**
   * Optional headline rendered above `tooltipHeader` to call out the
   * parent module that the chip represents. Module chips set this so
   * the tooltip reads "<Module> enabled\n\n<Module> add-ons enabled:
   * …"; the "Other" chip leaves it blank because there's no parent.
   */
  tooltipLead?: string;
  emptyTooltip: string;
  alwaysShowCount?: boolean;
}) {
  const count = enabledKeys.length;
  const showCount = alwaysShowCount || count > 0;

  return (
    <Tooltip
      title={
        <EnabledFlagsTooltipBody
          enabledKeys={enabledKeys}
          header={tooltipHeader}
          emptyText={emptyTooltip}
          lead={tooltipLead}
        />
      }
      arrow
    >
      <Chip
        size="small"
        // Default (filled gray, no border) to match the Tags chips on
        // the Feature Flags grid.
        // Tighten the right-side padding so the trailing count pill
        // sits flush with the chip border, mirroring MUI's
        // left-avatar chrome. The default `MuiChip-label` has
        // paddingRight: 12px (or 8px for size="small"); we trim it on
        // the trailing side when a pill is present so the pill feels
        // integrated rather than floating in whitespace.
        sx={
          showCount
            ? {
                "& .MuiChip-label": { pr: 0.5 },
              }
            : undefined
        }
        label={
          showCount ? (
            <Stack direction="row" alignItems="center" spacing={0.75} component="span">
              <Box component="span">{label}</Box>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 18,
                  minWidth: 22,
                  px: 0.5,
                  // Fully rounded "pill" so single-digit counts read
                  // as a circle and double-digit counts (+10, +12)
                  // grow horizontally without feeling squashed in a
                  // fixed-width avatar.
                  borderRadius: 999,
                  // White fill against the chip's gray body — the
                  // count "punches out" of the chip background so
                  // the "+N" pill reads clearly without an extra
                  // border. Text stays muted so the module name is
                  // the dominant element on the chip.
                  bgcolor: "common.white",
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
                data-testid="tenant-flag-chip-count-avatar"
              >
                +{count}
              </Box>
            </Stack>
          ) : (
            label
          )
        }
      />
    </Tooltip>
  );
}
